'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { isCustomObjectReport } from 'reporting-data/lib/customObjects';
import { isReportGrouping } from 'reporting-data/report/reportGetters';
import { hasSufficientReportEditPermission, isCustom, isCommercePaymentsReport, isContactJourney, isDealJourney } from '../../../lib/report-logic';
import { DOWNGRADE_ARTICLE_URL, getUpgradeUrl } from '../../../lib/linkUrls';
import { useCustomReportCount } from '../../../data/hooks/useReportCounts';
import { useHasFunnelJourneyAccess } from '../../../access/scopes/hooks/useHasFunnelJourneyAccess';
import { useHasRequiredAttributionReportScope } from '../../../access/scopes/hooks/useHasRequiredAttributionReportScope';
import { useHasCustomObjectAccess, useHasCustomReportingAccess, useHasReportingCreateAndOwnAccess, useHasReportingEditAccess, useHasPaymentsReportingAccess, useHasMarketingJourneyAccess, useHasSalesJourneyAccess } from '../../../access/scopes/hooks/useHasScope';
import useCustomReportLimit from '../../../access/limits/hooks/useCustomReportLimit';
import { CUSTOM_REPORT_LIMIT_EXCEEDED, INSUFFICIENT_ASSET_PERMISSION_LEVEL, INSUFFICIENT_REPORTING_PERMISSION_LEVEL, LACKS_ATTRIBUTION_REPORTING, LACKS_CUSTOM_OBJECT_ACCESS, LACKS_CUSTOM_REPORTING, WILL_EXCEED_CUSTOM_REPORT_LIMIT, LACKS_FIELD_ACCESS, LACKS_PAYMENTS_REPORTING_ACCESS, LACKS_SNOWFLAKE_TEMPLATE_ACCESS, LACKS_MARKETING_JOURNEY_REPORTING, LACKS_SALES_JOURNEY_REPORTING, UNSUPPORTED_REPORT_GROUPING, LACKS_ORG_ADMIN_ACCESS } from '../../problems';
import { ATTRIBUTION_UPGRADE_PRODUCT_TYPES, getAttributionReportUpgradeProduct } from '../upgradePoints/upgradeProducts';
import { getRestrictedFieldLevelProperties } from '../../../data/schemas/reportSchema';
import RestrictedFieldsProblemMessage from '../RestrictedFieldsProblemMessage';
import { useCheckSnowflakeTemplateAccess } from '../../../access/hooks/useCheckSnowflakeTemplateAccess';
import { useCheckOrgAdminAccess } from '../../../data/hooks/useCheckOrgAdminAccess';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
const useEditReportProblem = ({
  report,
  shouldBecomeCustom = false
}) => {
  const lacksSnowflakeTemplateAccess = useCheckSnowflakeTemplateAccess(report);
  const {
    data: lacksRequiredOrgAdminAccess
  } = useCheckOrgAdminAccess(report);
  const customReportCount = useCustomReportCount();
  const customReportLimit = useCustomReportLimit();
  const hasCustomObjectAccess = useHasCustomObjectAccess();
  const hasCustomReportingAccess = useHasCustomReportingAccess();
  const hasRequiredAttributionReportAccess = useHasRequiredAttributionReportScope(report);
  const hasMarketingJourneyAccess = useHasMarketingJourneyAccess();
  const hasSalesJourneyAccess = useHasSalesJourneyAccess();
  const hasFunnelJourneyAccess = useHasFunnelJourneyAccess();
  const hasPaymentsReportingAccess = useHasPaymentsReportingAccess();
  const hasReportingEditAccess = useHasReportingEditAccess();
  const hasReportingCreateAndOwnAccess = useHasReportingCreateAndOwnAccess();
  const isCustomReport = isCustom(report);
  const willBeCustomReport = shouldBecomeCustom || isCustomReport;
  const willIncreaseCustomReportCount = shouldBecomeCustom && !isCustomReport;
  const hasExceededCustomReportLimit = customReportCount > customReportLimit;
  const willExceedCustomReportLimit = willIncreaseCustomReportCount && customReportCount === customReportLimit;

  //users must have create permission to convert a standard report to a custom one by editing it
  const hasSufficientReportingPermission = willIncreaseCustomReportCount ? hasReportingCreateAndOwnAccess : hasReportingEditAccess;
  const restrictedFieldLevelProperties = getRestrictedFieldLevelProperties(report);
  if (!hasSufficientReportingPermission) {
    return {
      problem: INSUFFICIENT_REPORTING_PERMISSION_LEVEL,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.insufficientReportingPermission"
      })
    };
  } else if (!hasSufficientReportEditPermission(report)) {
    return {
      problem: INSUFFICIENT_ASSET_PERMISSION_LEVEL,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.insufficientAssetPermission"
      })
    };
  } else if (!hasRequiredAttributionReportAccess) {
    const requiresEnterprise = getAttributionReportUpgradeProduct(report) === ATTRIBUTION_UPGRADE_PRODUCT_TYPES.ENTERPRISE;
    return {
      problem: LACKS_ATTRIBUTION_REPORTING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: requiresEnterprise ? 'reporting-action-components.actions.editReport.button.tooltip.lacksEnterpriseAttribution' : 'reporting-action-components.actions.editReport.button.tooltip.lacksProAttribution'
      })
    };
  } else if (isContactJourney(report) && willBeCustomReport && !hasMarketingJourneyAccess && !hasFunnelJourneyAccess) {
    return {
      problem: LACKS_MARKETING_JOURNEY_REPORTING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.lacksMarketingJourneyReporting"
      })
    };
  } else if (isDealJourney(report) && willBeCustomReport && !hasSalesJourneyAccess && !hasFunnelJourneyAccess) {
    return {
      problem: LACKS_SALES_JOURNEY_REPORTING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.lacksSalesJourneyReporting"
      })
    };
  } else if (willBeCustomReport && !hasCustomReportingAccess) {
    return {
      problem: LACKS_CUSTOM_REPORTING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.lacksCustomReporting"
      })
    };
  } else if (isCustomObjectReport(report) && !hasCustomObjectAccess) {
    return {
      problem: LACKS_CUSTOM_OBJECT_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.lacksCustomObjects_jsx",
        elements: {
          UILink: Link
        },
        options: {
          href: DOWNGRADE_ARTICLE_URL
        }
      })
    };
  } else if (restrictedFieldLevelProperties.length) {
    return {
      problem: LACKS_FIELD_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(RestrictedFieldsProblemMessage, {
        message: 'reporting-action-components.actions.editReport.button.tooltip.restrictedFieldLevelPermission',
        restrictedFieldLevelProperties: restrictedFieldLevelProperties
      })
    };
  }
  //TODO: maybe add datatype access check
  else if (willBeCustomReport && hasExceededCustomReportLimit) {
    return {
      problem: CUSTOM_REPORT_LIMIT_EXCEEDED,
      disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.customReportLimitExceeded_jsx",
        elements: {
          UILink: Link
        },
        options: {
          href: getUpgradeUrl(),
          limit: customReportLimit
        }
      })
    };
  } else if (willExceedCustomReportLimit) {
    return {
      problem: WILL_EXCEED_CUSTOM_REPORT_LIMIT,
      disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.willExceedCustomReportLimit_jsx",
        elements: {
          UILink: Link
        },
        options: {
          href: getUpgradeUrl(),
          limit: customReportLimit
        }
      })
    };
  } else if (isCommercePaymentsReport({
    report
  }) && !hasPaymentsReportingAccess) {
    return {
      problem: LACKS_PAYMENTS_REPORTING_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'reporting-action-components.actions.editReport.button.tooltip.lacksPaymentsReportingAccess'
      })
    };
  } else if (lacksSnowflakeTemplateAccess) {
    return {
      problem: LACKS_SNOWFLAKE_TEMPLATE_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'reporting-action-components.actions.editReport.button.tooltip.lacksSnowflakeTemplateAccess'
      })
    };
  } else if (isReportGrouping(report)) {
    return {
      problem: UNSUPPORTED_REPORT_GROUPING,
      disabledMessage: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.unsupportedReportGrouping"
      })
    };
  } else if (lacksRequiredOrgAdminAccess) {
    return {
      problem: LACKS_ORG_ADMIN_ACCESS,
      disabledMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.editReport.button.tooltip.insufficientAssetPermission"
      })
    };
  }
  return {
    problem: null,
    disabledMessage: null
  };
};
export default useEditReportProblem;